import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient } from '@services/api';
import { useTranslationStore } from '@stores/translationStore';
import { useSessionStore } from '@stores/sessionStore';
import PageSpinner from '@components/PageSpinner';
import PageHeader from '@components/PageHeader';
import './GuidesPage.scss';

// Get hotel slug from URL params or localStorage fallback
const getHotelSlug = (paramSlug: string | undefined): string | null => {
  if (paramSlug && paramSlug !== 'undefined') return paramSlug;
  return localStorage.getItem('gcxp_hotel_slug');
};

interface StepAttachment {
  id: number;
  attachmentType: 'photo' | 'video' | 'audio' | 'pdf';
  filePath: string;
  thumbnailPath?: string;
  caption?: string;
  captionKey?: string;
  altText?: string;
}

interface GuideStep {
  id: number;
  stepNumber: number;
  title: string;
  titleKey: string;
  content?: string;
  contentKey?: string;
  autoAdvanceSeconds?: number;
  requireConfirmation: boolean;
  attachments: StepAttachment[];
  dynamicData?: { fieldName: string; displayName: string; value: unknown }[];
}

interface GuideDetail {
  id: number;
  categoryId: number;
  categoryName: string;
  title: string;
  titleKey: string;
  description?: string;
  descriptionKey?: string;
  iconEmoji: string;
  thumbnailPath?: string;
  autoAdvance: boolean;
  autoAdvanceIntervalSeconds: number;
  allowSkipSteps: boolean;
  showProgressBar: boolean;
  showStepNumbers: boolean;
  steps: GuideStep[];
}

interface GuideListItem {
  id: number;
  categoryId: number;
  categoryName: string;
  title: string;
  titleKey: string;
  description?: string;
  descriptionKey?: string;
  iconEmoji: string;
  thumbnailPath?: string;
  stepCount: number;
  isFeatured: boolean;
}

interface GuideCategory {
  id: number;
  name: string;
  nameKey: string;
  description?: string;
  descriptionKey?: string;
  iconEmoji: string;
  iconPath?: string;
  color?: string;
  guideCount: number;
}

interface GuidesConfig {
  enableGuestGuides: boolean;
  showCategorySidebar: boolean;
  defaultAutoAdvanceSeconds: number;
}

type ViewState = 'categories' | 'guides' | 'wizard';

export default function GuidesPage() {
  const { hotelSlug: paramSlug } = useParams();
  const hotelSlug = getHotelSlug(paramSlug);
  const { loadTranslations, t, language } = useTranslationStore();
  const { isValid, isLoading: sessionLoading } = useSessionStore();

  const [view, setView] = useState<ViewState>('categories');
  const [categories, setCategories] = useState<GuideCategory[]>([]);
  const [guides, setGuides] = useState<GuideListItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<GuideCategory | null>(null);
  const [selectedGuide, setSelectedGuide] = useState<GuideDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Wizard state
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [autoAdvanceTimer, setAutoAdvanceTimer] = useState<NodeJS.Timeout | null>(null);
  const [autoAdvanceCountdown, setAutoAdvanceCountdown] = useState<number>(0);
  const [stepsConfirmed, setStepsConfirmed] = useState<Set<number>>(new Set());

  useEffect(() => {
    const controller = new AbortController();
    // Only fetch data once session is validated (token is ready)
    if (hotelSlug && isValid && !sessionLoading) {
      fetchCategories(controller.signal);
      fetchConfig(controller.signal);
    }
    if (hotelSlug && language) {
      loadTranslations(hotelSlug, 'guides', language);
    }
    return () => {
      controller.abort();
      if (autoAdvanceTimer) clearInterval(autoAdvanceTimer);
    };
  }, [hotelSlug, language, isValid, sessionLoading]);

  // Auto-advance logic
  useEffect(() => {
    if (!selectedGuide || view !== 'wizard') return;
    if (autoAdvanceTimer) {
      clearInterval(autoAdvanceTimer);
      setAutoAdvanceTimer(null);
    }

    const currentStep = selectedGuide.steps[currentStepIndex];
    if (!currentStep) return;

    // Use step-specific auto-advance or guide default
    const advanceTime = currentStep.autoAdvanceSeconds ?? 
      (selectedGuide.autoAdvance ? selectedGuide.autoAdvanceIntervalSeconds : null);

    if (advanceTime && advanceTime > 0 && !currentStep.requireConfirmation) {
      setAutoAdvanceCountdown(advanceTime);
      const timer = setInterval(() => {
        setAutoAdvanceCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            goToNextStep();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      setAutoAdvanceTimer(timer);
    } else {
      setAutoAdvanceCountdown(0);
    }

    return () => {
      if (autoAdvanceTimer) clearInterval(autoAdvanceTimer);
    };
  }, [currentStepIndex, selectedGuide, view]);

  const fetchCategories = async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      const response = await apiClient.get<GuideCategory[]>(`/api/v1/${hotelSlug}/guides/categories`, { signal });
      if (response.data) {
        setCategories(response.data);
      }
    } catch (err: unknown) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      console.error('Failed to load categories:', err);
      setError('Failed to load guides');
    } finally {
      setLoading(false);
    }
  };

  const fetchGuides = async (categoryId?: number) => {
    try {
      setLoading(true);
      const url = categoryId 
        ? `/api/v1/${hotelSlug}/guides?categoryId=${categoryId}`
        : `/api/v1/${hotelSlug}/guides`;
      const response = await apiClient.get<GuideListItem[]>(url);
      if (response.data) {
        setGuides(response.data);
        setView('guides');
      }
    } catch (err) {
      console.error('Failed to load guides:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchGuideDetail = async (guideId: number) => {
    try {
      setLoading(true);
      const response = await apiClient.get<GuideDetail>(`/api/v1/${hotelSlug}/guides/${guideId}`);
      if (response.data) {
        setSelectedGuide(response.data);
        setCurrentStepIndex(0);
        setStepsConfirmed(new Set());
        setView('wizard');
      }
    } catch (err) {
      console.error('Failed to load guide:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async (signal?: AbortSignal) => {
    try {
      await apiClient.get<GuidesConfig>(`/api/v1/${hotelSlug}/guides/config`, { signal });
      // Config loaded - can be used for future customizations
    } catch (err) {
      console.error('Failed to load config:', err);
    }
  };

  const handleCategoryClick = (category: GuideCategory) => {
    setSelectedCategory(category);
    fetchGuides(category.id);
  };

  const handleGuideClick = (guide: GuideListItem) => {
    fetchGuideDetail(guide.id);
  };

  const goToNextStep = useCallback(() => {
    if (!selectedGuide) return;
    const currentStep = selectedGuide.steps[currentStepIndex];
    
    // If step requires confirmation and not confirmed, don't advance
    if (currentStep?.requireConfirmation && !stepsConfirmed.has(currentStep.id)) {
      return;
    }

    if (currentStepIndex < selectedGuide.steps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      // Guide complete - go back to guides list
      setView('guides');
      setSelectedGuide(null);
    }
  }, [selectedGuide, currentStepIndex, stepsConfirmed]);

  const goToPrevStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  const goToStep = (index: number) => {
    if (!selectedGuide?.allowSkipSteps) return;
    setCurrentStepIndex(index);
  };

  const confirmStep = () => {
    if (!selectedGuide) return;
    const currentStep = selectedGuide.steps[currentStepIndex];
    if (currentStep) {
      setStepsConfirmed(prev => new Set(prev).add(currentStep.id));
      // If auto-advance not active, go to next when confirmed
      if (!autoAdvanceCountdown) {
        goToNextStep();
      }
    }
  };

  const getDisplayText = (text: string | undefined, key: string | undefined) => {
    if (key && t(key) !== key) return t(key);
    return text || '';
  };

  const renderAttachment = (attachment: StepAttachment) => {
    const caption = getDisplayText(attachment.caption, attachment.captionKey);
    
    switch (attachment.attachmentType) {
      case 'photo':
        return (
          <div className="attachment attachment--photo">
            <img 
              src={attachment.filePath} 
              alt={attachment.altText || caption}
              loading="lazy"
            />
            {caption && <p className="attachment__caption">{caption}</p>}
          </div>
        );
      case 'video':
        return (
          <div className="attachment attachment--video">
            <video controls>
              <source src={attachment.filePath} />
            </video>
            {caption && <p className="attachment__caption">{caption}</p>}
          </div>
        );
      case 'audio':
        return (
          <div className="attachment attachment--audio">
            <audio controls>
              <source src={attachment.filePath} />
            </audio>
            {caption && <p className="attachment__caption">{caption}</p>}
          </div>
        );
      case 'pdf':
        return (
          <div className="attachment attachment--pdf">
            <a href={attachment.filePath} target="_blank" rel="noopener noreferrer" className="pdf-link">
              📄 {caption || 'View PDF'}
            </a>
          </div>
        );
      default:
        return null;
    }
  };

  const renderDynamicData = (data: { fieldName: string; displayName: string; value: unknown }[]) => {
    if (!data || data.length === 0) return null;
    
    return (
      <div className="dynamic-data">
        {data.map((item, idx) => (
          <div key={idx} className="dynamic-data__item">
            <span className="dynamic-data__label">{item.displayName}:</span>
            <span className="dynamic-data__value">{String(item.value)}</span>
          </div>
        ))}
      </div>
    );
  };

  if (loading && !categories.length) {
    return <PageSpinner />;
  }

  if (error) {
    return (
      <div className="guides-page guides-page--error">
        <p>{error}</p>
        <button onClick={() => fetchCategories()}>{t('common.retry') || 'Retry'}</button>
      </div>
    );
  }

  // Wizard View
  if (view === 'wizard' && selectedGuide) {
    const currentStep = selectedGuide.steps[currentStepIndex];
    const progress = ((currentStepIndex + 1) / selectedGuide.steps.length) * 100;
    const isLastStep = currentStepIndex === selectedGuide.steps.length - 1;
    const isStepConfirmed = stepsConfirmed.has(currentStep?.id || 0);

    return (
      <div className="guides-page guides-page--wizard">
        {/* Header */}
        <PageHeader
          title={<>{selectedGuide.iconEmoji} {getDisplayText(selectedGuide.title, selectedGuide.titleKey)}</>}
          leading={
            <button onClick={() => { setView('guides'); setSelectedGuide(null); }} className="back-button">
              ← {t('common.back') || 'Back'}
            </button>
          }
        />

        {/* Progress Bar */}
        {selectedGuide.showProgressBar && (
          <div className="progress-bar">
            <div className="progress-bar__fill" style={{ width: `${progress}%` }} />
            <div className="progress-bar__text">
              {currentStepIndex + 1} / {selectedGuide.steps.length}
            </div>
          </div>
        )}

        {/* Step Dots (clickable if skip allowed) */}
        <div className="step-dots">
          {selectedGuide.steps.map((step, idx) => (
            <button
              key={step.id}
              onClick={() => goToStep(idx)}
              disabled={!selectedGuide.allowSkipSteps}
              className={`step-dot ${idx === currentStepIndex ? 'step-dot--active' : ''} ${idx < currentStepIndex ? 'step-dot--completed' : ''}`}
            >
              {selectedGuide.showStepNumbers && idx + 1}
            </button>
          ))}
        </div>

        {/* Current Step */}
        {currentStep && (
          <div className="wizard-step">
            <div className="step-number">
              Step {currentStep.stepNumber}
            </div>
            <h2 className="step-title">
              {getDisplayText(currentStep.title, currentStep.titleKey)}
            </h2>
            
            {currentStep.content && (
              <div className="step-content">
                {getDisplayText(currentStep.content, currentStep.contentKey)}
              </div>
            )}

            {/* Attachments */}
            {currentStep.attachments.length > 0 && (
              <div className="step-attachments">
                {currentStep.attachments.map(att => (
                  <div key={att.id}>
                    {renderAttachment(att)}
                  </div>
                ))}
              </div>
            )}

            {/* Dynamic Data */}
            {currentStep.dynamicData && renderDynamicData(currentStep.dynamicData)}

            {/* Auto-advance countdown */}
            {autoAdvanceCountdown > 0 && (
              <div className="auto-advance-indicator">
                {t('guides.nextIn') || 'Next in'} {autoAdvanceCountdown}s
              </div>
            )}

            {/* Confirmation button if required */}
            {currentStep.requireConfirmation && !isStepConfirmed && (
              <button onClick={confirmStep} className="confirm-button">
                ✓ {t('guides.iUnderstand') || 'I understand'}
              </button>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="wizard-nav">
          <button 
            onClick={goToPrevStep} 
            disabled={currentStepIndex === 0}
            className="nav-button nav-button--prev"
          >
            ← {t('guides.previous') || 'Previous'}
          </button>
          
          <button 
            onClick={goToNextStep}
            disabled={currentStep?.requireConfirmation && !isStepConfirmed}
            className="nav-button nav-button--next"
          >
            {isLastStep 
              ? (t('guides.finish') || 'Finish') 
              : (t('guides.next') || 'Next')} →
          </button>
        </div>
      </div>
    );
  }

  // Guides List View
  if (view === 'guides') {
    return (
      <div className="guides-page guides-page--list">
        <PageHeader
          title={<>{selectedCategory?.iconEmoji} {getDisplayText(selectedCategory?.name, selectedCategory?.nameKey)}</>}
          leading={
            <button onClick={() => { setView('categories'); setSelectedCategory(null); }} className="back-button">
              ← {t('common.back') || 'Back'}
            </button>
          }
        />

        <div className="guides-grid">
          {guides.map(guide => (
            <button 
              key={guide.id} 
              onClick={() => handleGuideClick(guide)}
              className="guide-card"
            >
              {guide.thumbnailPath ? (
                <img src={guide.thumbnailPath} alt="" className="guide-card__image" />
              ) : (
                <div className="guide-card__emoji">{guide.iconEmoji}</div>
              )}
              <div className="guide-card__content">
                <h3 className="guide-card__title">
                  {getDisplayText(guide.title, guide.titleKey)}
                </h3>
                {guide.description && (
                  <p className="guide-card__description">
                    {getDisplayText(guide.description, guide.descriptionKey)}
                  </p>
                )}
                <span className="guide-card__steps">
                  {guide.stepCount} {t('guides.steps') || 'steps'}
                </span>
              </div>
              {guide.isFeatured && <span className="guide-card__featured">⭐</span>}
            </button>
          ))}
        </div>

        {guides.length === 0 && (
          <div className="empty-state">
            <p>{t('guides.noGuides') || 'No guides available in this category'}</p>
          </div>
        )}
      </div>
    );
  }

  // Categories View (default)
  return (
    <div className="guides-page guides-page--categories">
      <PageHeader
        title={<>📖 {t('guides.title') || 'Guides'}</>}
        subtitle={t('guides.subtitle') || 'Step-by-step instructions to help you during your stay'}
      />

      <div className="categories-grid">
        {categories.map(category => (
          <button 
            key={category.id} 
            onClick={() => handleCategoryClick(category)}
            className="category-card"
            style={category.color ? { borderColor: category.color } : undefined}
          >
            <div className="category-card__icon">
              {category.iconPath ? (
                <img src={category.iconPath} alt="" />
              ) : (
                <span className="category-card__emoji">{category.iconEmoji}</span>
              )}
            </div>
            <div className="category-card__content">
              <h3 className="category-card__title">
                {getDisplayText(category.name, category.nameKey)}
              </h3>
              {category.description && (
                <p className="category-card__description">
                  {getDisplayText(category.description, category.descriptionKey)}
                </p>
              )}
              <span className="category-card__count">
                {category.guideCount} {t('guides.guides') || 'guides'}
              </span>
            </div>
          </button>
        ))}
      </div>

      {categories.length === 0 && (
        <div className="empty-state">
          <p>{t('guides.noCategories') || 'No guides available yet'}</p>
        </div>
      )}
    </div>
  );
}
